import { useCallback, useContext, useEffect, useState } from 'react';
import { ServiceContext } from '../../context/ServiceContext';
import { useAuth } from '../use-auth';
import { setSessionData } from '../../utils/session-utils';

function isError(err: unknown): err is Error {
  return err instanceof Error;
}

/**
 * Fetches data from authService fetch, or returns data from session if available.
 * If not authenticated, returns { data: null, loading: false, error: null, refetch: () => Promise<unknown> }
 * @param {string} url
 * @param {RequestInit} [options]
 * @param {string} [sessionKey]
 * @returns auth json object
 */
export const useAuthFetch = (url: string, options = {}, sessionKey?: string) => {
  const { isAuthenticated } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { authService } = useContext(ServiceContext);

  const fetchData = useCallback(
    async (forceRefresh = false) => {
      // Check session first (unless forcing refresh)
      if (!forceRefresh) {
        const sessionData =
          typeof window !== 'undefined' && sessionStorage?.getItem(sessionKey || '');
        if (sessionData) {
          const parsedSession = JSON.parse(sessionData);
          setData(parsedSession.data);
          setLoading(false);
          return parsedSession.data;
        }
      }

      setLoading(true);
      setError(null);

      try {
        const response = await authService.fetch(url, options);
        if (!response.ok) {
          console.error('Auth HTTP error! status: ', response.status);
        }

        const result = await response.json();

        // save the result to session
        if (sessionKey) setSessionData(sessionKey, result);
        setData(result);

        return result;
      } catch (err: unknown) {
        if (isError(err)) {
          setError(err.message);
        } else {
          setError(String(err));
        }
      } finally {
        setLoading(false);
      }
    },
    [url, isAuthenticated],
  );

  useEffect(() => {
    if (isAuthenticated) fetchData();
  }, [fetchData, isAuthenticated]);

  return { data, loading, error, refetch: () => fetchData(true) };
};
